import React from "react";

import { FlightCardDetailsHeader } from "../FlightCardDetailsHeader";
import { FlightCardDetailsSegment } from "../FlightCardDetailsSegment";
import { FlightDetailsSubHeader } from "../FlightDetailsSubHeader";
import { StopDivision } from "../StopDivision";

import { Typography } from "Components/atoms/Typography";
import { FlightDetailsContainer, FlightDetailsTopo } from "./styles";
import { FlightDetailsProps } from "./types";
import {
  calculateWaitingTime,
  convertDayOfWeekToText,
  convertDurationToText,
  convertToHHMM,
  formatDateDDMMYYYY,
} from "shared/utils/dates";
import { getAirports } from "shared/services/contexts/airport.service";

/**
 * Componente FlightDetails
 *
 * Exibe os detalhes dos voos de ida e volta, incluindo paradas e durações.
 *
 * @param {SERPFlightSector} sectors - Detalhes dos setores.
 * @param {Function} onClose - Callback para fechar os detalhes do voo.
 */
export const FlightDetails: React.FC<FlightDetailsProps> = ({
  sectors,
  onClose,
}) => {
  const { findAirportByCode } = getAirports();

  return (
    <>
      <FlightDetailsTopo>
        <Typography variant="caption" color="black" weight="extraLight">
          Confira os preços e detalhes da viagem antes de ir ao site vendedor.
        </Typography>
      </FlightDetailsTopo>

      <FlightDetailsContainer>
        <FlightCardDetailsHeader onClose={onClose} />

        {/* Para cada Setor */}
        {sectors?.map((sector, sectorIndex) => (
          <div key={`sector-${sectorIndex}`}>
            {/* Voos de Ida */}
            {sectorIndex === 0 && (
              <div key={`ida-${sectorIndex}`}>
                <FlightDetailsSubHeader
                  title="Ida"
                  departureAirport={sector.departureAirportIata}
                  arrivalAirport={sector.arrivalAirportIata}
                  date={formatDateDDMMYYYY(sector.departureDate)}
                  dayOfWeek={convertDayOfWeekToText(sector.departureDayOfWeek)}
                  duration={convertDurationToText(sector.totalDuration)}
                  stops={sector.stops}
                />

                {/* Para cada Voo */}
                {sector.flights?.map((flight, flightIndex) => (
                  <div key={`${flight.flightNumber}-${flightIndex}`}>
                    <FlightCardDetailsSegment
                      departureAirportDescription={
                        findAirportByCode(flight.departureAirportIata)
                          ?.description ?? ""
                      }
                      arrivalAirportDescription={
                        findAirportByCode(flight.arrivalAirportIata)
                          ?.description ?? ""
                      }
                      airlineName={flight.airline}
                      flightNumber={flight.flightNumber}
                      departureTime={convertToHHMM(flight.departureTime)}
                      departureAirport={flight.departureAirportIata}
                      arrivalTime={convertToHHMM(flight.arrivalTime)}
                      arrivalAirport={flight.arrivalAirportIata}
                      duration={convertDurationToText(flight.totalDuration)}
                    />
                    {sector.flights.length > 1 &&
                      flightIndex < sector.flights.length - 1 &&
                      (() => {
                        const waitingTime = calculateWaitingTime(
                          sector.flights[flightIndex],
                          sector.flights[flightIndex + 1],
                        );

                        return waitingTime ? (
                          <StopDivision
                            waitingTime={waitingTime}
                            key={`stop-${sector.flights[flightIndex].flightNumber}-${flightIndex}`}
                          />
                        ) : null;
                      })()}
                  </div>
                ))}
              </div>
            )}

            {/* Voos de Volta */}
            {sectorIndex === 1 && (
              <div key={`volta-${sectorIndex}`}>
                <FlightDetailsSubHeader
                  title="Volta"
                  departureAirport={sector.departureAirportIata}
                  arrivalAirport={sector.arrivalAirportIata}
                  date={formatDateDDMMYYYY(sector.departureDate)}
                  dayOfWeek={convertDayOfWeekToText(sector.departureDayOfWeek)}
                  duration={convertDurationToText(sector.totalDuration)}
                  stops={sector.stops}
                />

                {/* Para cada Voo */}
                {sector.flights?.map((flight, flightIndex) => (
                  <div key={`${flight.flightNumber}-${flightIndex}`}>
                    <FlightCardDetailsSegment
                      departureAirportDescription={
                        findAirportByCode(flight.departureAirportIata)
                          ?.description ?? ""
                      }
                      arrivalAirportDescription={
                        findAirportByCode(flight.arrivalAirportIata)
                          ?.description ?? ""
                      }
                      airlineName={flight.airline}
                      flightNumber={flight.flightNumber}
                      departureTime={convertToHHMM(flight.departureTime)}
                      departureAirport={flight.departureAirportIata}
                      arrivalTime={convertToHHMM(flight.arrivalTime)}
                      arrivalAirport={flight.arrivalAirportIata}
                      duration={convertDurationToText(flight.totalDuration)}
                    />
                    {sector.flights.length > 1 &&
                      flightIndex < sector.flights.length - 1 &&
                      (() => {
                        const waitingTime = calculateWaitingTime(
                          sector.flights[flightIndex],
                          sector.flights[flightIndex + 1],
                        );

                        return waitingTime ? (
                          <StopDivision
                            waitingTime={waitingTime}
                            key={`stop-${sector.flights[flightIndex].flightNumber}-${flightIndex}`}
                          />
                        ) : null;
                      })()}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </FlightDetailsContainer>
    </>
  );
};
